import { ReturnOrderListItemV2 } from 'services/api/refund';
import { useReturnReasonLabel } from 'views/Conversation/Sider/hooks/useReturnReasonLabel';
import { DetailCard } from '../DetailCard';
import { useTranslate } from '@tolgee/react';
import { useQuery } from '@tanstack/react-query';
import { queries } from 'services/queries';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { Button, Skeleton } from 'antd';
import { useToggle } from 'ahooks';
import { useEffect, useMemo } from 'react';
import { MediaViewer, MediaItem } from 'components/MediaViewer';
import { usePlatformResourceURL } from 'hooks/usePlatformResourceURL';

interface OrderReturnReasonProps {
  platform: General.PlatformUpperCase;
  returnOrder: ReturnOrderListItemV2;
}

export const OrderReturnReason = (props: OrderReturnReasonProps) => {
  const { platform, returnOrder } = props;
  const { reason, reasonName, reassessedRequestReason, textReason, returnImage, returnVideo } =
    returnOrder;

  const systemLanguage = useSystemLanguageValue();
  const { t } = useTranslate();

  const { reasonLabel, reassessedRequestReasonLabel } = useReturnReasonLabel({
    platform,
    reason,
    reasonName,
    reassessedRequestReason,
  });

  const { getImageResource, getVideoResource } = usePlatformResourceURL();

  const hasTextReason = textReason?.trim() !== '';

  const { isLoading, data: textReasonTrans } = useQuery({
    ...queries.translates.overseaTranslate(textReason, systemLanguage, 'drawer'),
    enabled: hasTextReason,
  });

  const mediaItems = useMemo<MediaItem[]>(() => {
    const images = (returnImage ?? []).map((src) => {
      const { src: imageSrc, thumb: imageThumb } = getImageResource(src, platform);
      return {
        type: 'image' as const,
        src: imageSrc,
        thumb: imageThumb,
      };
    });
    const videos = (returnVideo ?? []).map((item) => {
      if (typeof item === 'string') {
        const { src: videoSrc } = getVideoResource(item, platform);
        return { type: 'video' as const, src: videoSrc };
      }
      const { src: videoSrc, thumb: videoThumb } = getVideoResource(
        item.videoUrl,
        platform,
        item.thumbnailUrl,
      );
      return {
        type: 'video' as const,
        src: videoSrc,
        thumb: videoThumb,
      };
    });
    return [...images, ...videos];
  }, [getImageResource, getVideoResource, platform, returnImage, returnVideo]);

  const hasMedia = mediaItems.length > 0;
  const hasContent = hasTextReason || hasMedia;

  const [viewOriginal, { toggle: toggleViewOriginal }] = useToggle(true);

  useEffect(() => {
    if (textReasonTrans) {
      toggleViewOriginal();
    }
  }, [textReasonTrans, toggleViewOriginal]);

  return (
    <>
      <DetailCard
        title={reasonLabel || t('trade.退货/退款原因')}
        rightExtra={
          hasTextReason && (
            <Button type="link" className="!px-0" onClick={toggleViewOriginal}>
              {viewOriginal ? t('trade.查看翻译') : t('trade.查看原文')}
            </Button>
          )
        }
      >
        {hasContent && (
          <div className="text-[var(--fett-text-primary)] bg-[var(--fett-bg-subtle)] rounded p-3 flex flex-col gap-2">
            {hasTextReason && (
              <>
                {isLoading ? (
                  <Skeleton.Input size="small" className="!w-48" active />
                ) : viewOriginal ? (
                  textReason
                ) : (
                  textReasonTrans
                )}
              </>
            )}
            {hasMedia && (
              <MediaViewer
                items={mediaItems}
                thumbSize={48}
                gap={12}
                maxThumbs={12}
                photoClosable
              />
            )}
          </div>
        )}
      </DetailCard>
      {!!reassessedRequestReasonLabel && (
        <DetailCard title={t('复核后退货退款原因')}>{reassessedRequestReasonLabel}</DetailCard>
      )}
    </>
  );
};
